import * as Sentry from '@sentry/react';

import { getUserIdFromToken } from '@/common/domain/auth/jwt';
import {
  setAuthTokenGetter as setBaseAuthTokenGetter,
  setUnauthorizedHandler as setBaseUnauthorizedHandler,
  type AuthTokenGetter,
  type UnauthorizedHandler
} from '@/common/infrastructure/http/authToken';
import { clearCorrelationId, generateCorrelationId, setCorrelationUserKey } from '@/common/infrastructure/observability/correlationIdUtils';

export type { AuthTokenGetter, UnauthorizedHandler };

function syncSentryUserFromToken(token: string | null): void {
  const userId = getUserIdFromToken(token);
  setCorrelationUserKey(userId);

  if (userId) {
    Sentry.setUser({ id: userId });
    generateCorrelationId();
    return;
  }

  Sentry.setUser(null);
}

/**
 * Module Federation surface for the host to register JWT retrieval before routes mount.
 * Identifies the user in Sentry (id only — no PII) and refreshes the correlation id.
 */
export function setAuthTokenGetter(getter: AuthTokenGetter): void {
  setBaseAuthTokenGetter(async () => {
    const token = await getter();
    syncSentryUserFromToken(token);
    return token;
  });
}

/** Clears Sentry user context and correlation id, then invokes the host handler. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  setBaseUnauthorizedHandler(() => {
    Sentry.setUser(null);
    clearCorrelationId();
    handler();
  });
}
